import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { m } from './shared';

interface TrialDialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

/**
 * The design's trial `<dialog>`: every trial CTA opens the page's own signup form in a native modal (top layer,
 * focus trapped by the browser, Esc to close); closing it hands the form back to the hero. The caller decides
 * what renders inside; this only drives `showModal()` / `close()` from `open`.
 */
export function TrialDialog({ open, onClose, children }: TrialDialogProps) {
  const { t } = useTranslation('pages/trial-matrix');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (open && !dialog.open && typeof dialog.showModal === 'function') {
      dialog.showModal();
      const timer = window.setTimeout(() => dialog.querySelector<HTMLInputElement>('input:not([type=hidden])')?.focus({ preventScroll: true }), 50);
      return () => {
        window.clearTimeout(timer);
        if (dialog.open && typeof dialog.close === 'function') dialog.close();
        if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
      };
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const handleClose = () => onCloseRef.current();
    const handleBackdrop = (event: MouseEvent) => {
      if (event.target === dialog && typeof dialog.close === 'function') dialog.close();
    };
    dialog.addEventListener('close', handleClose);
    dialog.addEventListener('click', handleBackdrop);
    return () => {
      dialog.removeEventListener('close', handleClose);
      dialog.removeEventListener('click', handleBackdrop);
    };
  }, []);

  return (
    <dialog ref={dialogRef} className={m('np-trial-dialog')} aria-label={t('lp.dialog.label')}>
      <div className='relative m-auto w-full max-w-[520px] rounded-[20px] border border-[#93FF18]/[.16] bg-[#0B110D] shadow-[0_24px_80px_rgb(0_0_0/.6)] max-[520px]:rounded-[14px] forced-colors:border-[CanvasText]'>
        <button className='absolute end-2 top-2 z-[5] grid size-11 cursor-pointer place-items-center rounded-full border border-white/30 bg-[#122017] text-white transition-colors hover:border-[#93FF18]/60 hover:bg-[#16281c] focus-visible:rounded-full' type='button' aria-label={t('lp.dialog.close')} onClick={() => dialogRef.current?.close?.()}>
          <svg className='size-5' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' aria-hidden='true' focusable='false'>
            <path d='M6 6l12 12M18 6 6 18' />
          </svg>
        </button>
        <div className='px-3 pb-3 pt-[22px] max-[520px]:px-1 max-[520px]:pb-1 max-[520px]:pt-14'>{open ? children : null}</div>
      </div>
    </dialog>
  );
}
